import { Button, Tag } from 'antd';
import Table, { ColumnsType, TableProps } from 'antd/es/table';
import { SortOrder } from 'antd/es/table/interface';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, Link } from 'react-router';

import { AdminOrganizationListStreamSortBy } from 'protocol/api/core/svc_adminka_pb';

import { PATHS } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';

import {
  OrganizationListFilter,
  OrganizationListFilterCase,
  organizationsModel,
  useOrganizationListStream,
} from '@/entities/organizations';

import { useCountryName } from '@/features/countries';

import {
  COLUMN_KEY_TO_SORT_BY,
  EMPTY_SUBSCRIPTION_END_DATE_FILTER,
  SubscriptionEndDateFilterValue,
} from './config';
import { isSubscriptionEndDateFiltered } from './lib';
import styles from './OrganizationsTab.module.scss';
import { SubscriptionEndDateFilter } from './SubscriptionEndDateFilter';

type OrganizationsTableDataItem = {
  id: string;
  key: string;
  name: string;
  email: string;
  ownerEmail: string;
  country: string;
  created: string;
  subscriptionEndDate: string;
  isSubscriptionActive: boolean;
  isDistributor: boolean;
  distributorEmail: string;
  distributorName: string;
};

type OrganizationsTabProps = {
  debouncedSearch: string;
  filterCase?: OrganizationListFilterCase;
};

const useOrganizationsTableData = (): OrganizationsTableDataItem[] => {
  const { formatDate } = useIntl();
  const organizations = useAppSelector(organizationsModel.selectors.selectAll);
  const getCountryName = useCountryName();

  return useMemo(
    () =>
      organizations.map((organization): OrganizationsTableDataItem => {
        const country = getCountryName(organization.Contacts?.Country);

        return {
          id: organization.ID,
          key: organization.ID,
          name: organization.Name || '',
          email: organization.Contacts?.Emails?.[0] || '',
          ownerEmail: organization.OwnerEmail || '',
          country,
          created: organization.Created?.At?.seconds
            ? formatDate(
                new Date(Number(organization.Created.At.seconds) * 1000),
                {
                  year: 'numeric',
                  month: 'long',
                  day: '2-digit',
                },
              )
            : '',
          // The date is shown whether the subscription is still running or
          // not — an expired one is dimmed, not hidden. "No subscription" is
          // left for an organization that has never held one.
          subscriptionEndDate: organization.Billing?.SubscriptionEndDate
            ?.seconds
            ? formatDate(
                new Date(
                  Number(organization.Billing.SubscriptionEndDate.seconds) *
                    1000,
                ),
                {
                  year: 'numeric',
                  month: 'long',
                  day: '2-digit',
                },
              )
            : '',
          isSubscriptionActive: !!organization.Billing?.IsSubscriptionActive,
          isDistributor: !!organization.Distributor,
          distributorEmail: organization.OnboardedByDistributor?.Email || '',
          distributorName: organization.OnboardedByDistributor?.Name || '',
        };
      }),
    [organizations, getCountryName, formatDate],
  );
};

const useSortOrganizationsTable = () => {
  const [sortOrder, setSortOrder] = useState<SortOrder>();
  const [sortBy, setSortBy] = useState<AdminOrganizationListStreamSortBy>();

  const handleTableSortChange: TableProps<OrganizationsTableDataItem>['onChange'] =
    (pagination, filters, sorter) => {
      const currentSorter = Array.isArray(sorter) ? sorter[0] : sorter;

      // The third click on a column clears the sorting: the column is still
      // reported, the order is not.
      const nextSortBy = currentSorter.order
        ? COLUMN_KEY_TO_SORT_BY[String(currentSorter.columnKey)]
        : undefined;

      setSortBy(nextSortBy);
      setSortOrder(nextSortBy ? currentSorter.order : undefined);
    };

  return {
    sortBy,
    sortOrder,
    handleTableSortChange,
  };
};

export const OrganizationsTab: FC<OrganizationsTabProps> = (props) => {
  const { debouncedSearch, filterCase } = props;

  const organizations = useAppSelector(organizationsModel.selectors.selectAll);
  const loading = useAppSelector(organizationsModel.selectors.selectLoading);

  const dispatch = useAppDispatch();
  const { openOrganizationListStream, closeOrganizationListStream } =
    useOrganizationListStream();
  const organizationsTableData = useOrganizationsTableData();
  const { sortBy, sortOrder, handleTableSortChange } =
    useSortOrganizationsTable();

  const [subscriptionEndDateFilter, setSubscriptionEndDateFilter] =
    useState<SubscriptionEndDateFilterValue>(
      EMPTY_SUBSCRIPTION_END_DATE_FILTER,
    );
  const [isSubscriptionEndDateFilterOpen, setSubscriptionEndDateFilterOpen] =
    useState(false);

  const lastOrganizationID = useMemo(
    () => organizations.at(-1)?.ID,
    [organizations],
  );

  const listFilter = useMemo<OrganizationListFilter>(
    () => (filterCase ? { case: filterCase, value: {} } : undefined),
    [filterCase],
  );

  const handleShowMore = useCallback(() => {
    closeOrganizationListStream(false);

    openOrganizationListStream({
      startFromOrganizationID: lastOrganizationID,
      search: debouncedSearch,
      sortBy,
      sortOrder,
      filter: listFilter,
      subscriptionEndDateFrom: subscriptionEndDateFilter.from,
      subscriptionEndDateTo: subscriptionEndDateFilter.to,
      onlyActiveSubscriptions: subscriptionEndDateFilter.onlyActive,
    });
  }, [
    closeOrganizationListStream,
    openOrganizationListStream,
    debouncedSearch,
    lastOrganizationID,
    sortBy,
    sortOrder,
    listFilter,
    subscriptionEndDateFilter,
  ]);

  const columns: ColumnsType<OrganizationsTableDataItem> = [
    {
      key: 'name',
      title: <FormattedMessage id="organizations.name" defaultMessage="Name" />,
      dataIndex: 'name',
      sorter: true,
      width: 200,
      className: styles.wrapCell,
    },
    {
      key: 'email',
      title: (
        <FormattedMessage id="organizations.email" defaultMessage="E-mail" />
      ),
      dataIndex: 'ownerEmail',
      sorter: true,
      width: 100,
      className: styles.wrapCell,
    },
    {
      key: 'country',
      title: (
        <FormattedMessage id="organizations.country" defaultMessage="Country" />
      ),
      dataIndex: 'country',
      width: 2,
      className: styles.wrapCell,
    },
    {
      key: 'created',
      title: (
        <FormattedMessage id="organizations.created" defaultMessage="Created" />
      ),
      dataIndex: 'created',
      width: 2,
    },
    {
      key: 'subscriptionEndDate',
      title: (
        <FormattedMessage
          id="organizations.subscriptionEndDate"
          defaultMessage="Subscription end date"
        />
      ),
      dataIndex: 'subscriptionEndDate',
      sorter: true,
      width: 2,
      filtered: isSubscriptionEndDateFiltered(subscriptionEndDateFilter),
      filterDropdownOpen: isSubscriptionEndDateFilterOpen,
      onFilterDropdownOpenChange: setSubscriptionEndDateFilterOpen,
      filterDropdown: (
        <SubscriptionEndDateFilter
          open={isSubscriptionEndDateFilterOpen}
          value={subscriptionEndDateFilter}
          onChange={setSubscriptionEndDateFilter}
          onClose={() => setSubscriptionEndDateFilterOpen(false)}
        />
      ),
      render: (subscriptionEndDate: string, { isSubscriptionActive }) => {
        if (!subscriptionEndDate) {
          return (
            <FormattedMessage
              id="organizations.noSubscription"
              defaultMessage="No subscription"
            />
          );
        }

        return isSubscriptionActive ? (
          subscriptionEndDate
        ) : (
          <span className={styles.expiredSubscription}>
            {subscriptionEndDate}
          </span>
        );
      },
    },
    {
      key: 'isDistributor',
      title: (
        <FormattedMessage
          id="organizations.isDistributor"
          defaultMessage="Is Distributor"
        />
      ),
      dataIndex: 'isDistributor',
      width: 100,
      render: (isDistributor: boolean) =>
        isDistributor ? (
          <Tag color="blue">
            <FormattedMessage
              id="organizations.type.distributor"
              defaultMessage="Distributor"
            />
          </Tag>
        ) : null,
    },
    {
      key: 'distributorEmail',
      title: (
        <FormattedMessage
          id="organizations.distributor"
          defaultMessage="Distributor"
        />
      ),
      dataIndex: 'distributorEmail',
      width: 150,
      className: styles.wrapCell,
      render: (email: string, record) =>
        email ? (
          <div>
            <div>{email}</div>
            {record.distributorName && (
              <div style={{ fontSize: '12px', color: '#888' }}>
                {record.distributorName}
              </div>
            )}
          </div>
        ) : (
          '-'
        ),
    },
    {
      title: (
        <FormattedMessage id="organizations.action" defaultMessage="Action" />
      ),
      key: 'action',
      width: 1,
      render: (_, record) => (
        <Link
          className={styles.detailsLink}
          to={generatePath(PATHS.organization, { organizationID: record.id })}
        >
          <FormattedMessage
            id="organizations.detailsLink"
            defaultMessage="Details"
          />
        </Link>
      ),
    },
  ];

  useEffect(() => {
    dispatch(organizationsModel.actions.removeAll());

    openOrganizationListStream({
      search: debouncedSearch,
      sortBy,
      sortOrder,
      filter: listFilter,
      subscriptionEndDateFrom: subscriptionEndDateFilter.from,
      subscriptionEndDateTo: subscriptionEndDateFilter.to,
      onlyActiveSubscriptions: subscriptionEndDateFilter.onlyActive,
    });

    return () => {
      closeOrganizationListStream(false);
    };
  }, [
    debouncedSearch,
    sortBy,
    sortOrder,
    listFilter,
    subscriptionEndDateFilter,
    openOrganizationListStream,
    closeOrganizationListStream,
  ]);

  return (
    <div className={styles.container}>
      <Table
        columns={columns}
        dataSource={organizationsTableData}
        loading={loading}
        pagination={false}
        onChange={handleTableSortChange}
      />

      <Button
        type="text"
        onClick={handleShowMore}
        className={styles.showMoreButton}
      >
        <FormattedMessage id="global.showMore" defaultMessage="Show more" />
      </Button>
    </div>
  );
};
